import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Skeleton,
  alpha,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Check, Minus, Info } from "lucide-react";

const CircleCheck = ({ checked, indeterminate, onClick, title }) => {
  const active = checked || indeterminate;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      title={title}
      sx={{
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: active ? "hsl(150 38% 17%)" : "hsl(140 8% 88%)",
        backgroundColor: active ? "hsl(150 38% 17%)" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        padding: 0,
        transition: "all 200ms",
        "&:hover": active
          ? {}
          : {
            borderColor: "hsl(150 38% 17%)",
            backgroundColor: "hsla(150, 38%, 17%, 0.1)",
          },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: active ? "hsl(80, 97%, 72%)" : "hsl(150, 38%, 17%)",
          opacity: active ? 1 : 0,
          transition: "all 200ms",
        }}
      >
        {indeterminate && !checked ? (
          <Minus size={12} strokeWidth={3} />
        ) : (
          <Check size={12} strokeWidth={3} />
        )}
      </Box>
    </Box>
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

/**
 * Generic reusable data table component for internal use.
 *
 * Props:
 * ──────────────────── Data & Columns ────────────────────
 * @param {Array}    data               
 * @param {Array}    columns            – [{key, label}, …]
 * @param {Function} [renderCell]       – (row, key) → ReactNode  (default: formatValue)
 * @param {Array}    [extraHeadColumns] – extra column headers rendered *before* columns
 * @param {Function} [renderExtraCells] – (row) → <TableCell …/>  rendered before columns
 * @param {Array}    [tailHeadColumns]  – extra column headers rendered *after* columns
 * @param {Function} [renderTailCells]  – (row) → <TableCell …/>  rendered after columns
 *
 * ──────────────────── Row Interaction ────────────────────
 * @param {Function} [onRowClick]       – (row) => void
 *
 * ──────────────────── Expandable rows ────────────────────
 * @param {Function} [renderExpandedRow] – (row) => ReactNode rendered in a full-width sub-row
 * @param {boolean}  [expandable]        – renders a leading expand-arrow column (leftmost, before the checkbox)
 * @param {Function} [isRowExpanded]     – (row) => boolean  (drives the arrow direction)
 * @param {Function} [onToggleExpandRow] – (row, event) => void
 *
 * ──────────────────── Add Button ────────────────────
 * @param {Function} [onAddClick]
 * @param {string}   [addButtonLabel]   – "Add item"
 *
 * ──────────────────── Search ────────────────────
 * @param {Function} [onQueryChange]    – (query) => void
 * @param {string}   [initialQuery]     – seeds the search input on mount
 * @param {string}   [searchPlaceholder]
 * @param {ReactNode} [searchInfo]      – hover text (fields you can search from); renders an info icon in the search bar
 * @param {boolean}  [searchById]      – when true, the info tooltip also explains ID lookup (#123 or id: 123)
 *
 * ──────────────────── Filters ────────────────────
 * @param {ReactNode} [filtersComponent] – pre-built JSX to render below header
 *
 * ──────────────────── Pagination ────────────────────
 * @param {number}   page
 * @param {number}   totalCount
 * @param {number}   rowsPerPage
 * @param {Function} onPageChange       – (newPage) => void  (1-based)
 * @param {Function} onRowsPerPageChange
 *
 * ──────────────────── Selection / Bulk ────────────────────
 * @param {boolean}  [selectable]
 * @param {Array}    [selectedRows]
 * @param {Function} [onRowSelect]
 * @param {React.ComponentType} [bulkActionsComponent]
 * @param {object}   [bulkActionsProps]
 * @param {Function} [onExportCsv]      – (selectedIds) => void  (renders Export CSV button when provided)
 *
 * ──────────────────── Chrome ────────────────────
 * @param {boolean}  [loading]
 * @param {string}   [title]
 * @param {Function} [totalLabel]        – (count) => string
 * @param {React.ComponentType} [emptyIcon]
 * @param {string}   [emptyTitle]
 * @param {string}   [emptySubtitle]
 */
const DataTable = ({
  data = [],
  columns = [],
  renderCell,
  extraHeadColumns = [],
  renderExtraCells,
  tailHeadColumns = [],
  renderTailCells,
  renderExpandedRow,
  expandable = false,
  isRowExpanded,
  onToggleExpandRow,
  getRowSx,
  onRowClick,
  onAddClick,
  addButtonLabel = "Add item",
  addDisabled = false,
  addTooltip,
  extraActions = [],
  onQueryChange,
  initialQuery = "",
  searchPlaceholder = "Search by name, email…",
  searchInfo,
  searchById = false,
  filtersComponent,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  bulkActionsComponent: BulkActionsComponent,
  bulkActionsProps = {},
  onExportCsv,
  loading = false,
  title = "Items",
  totalLabel = (count) => `${count.toLocaleString()} total items`,
  emptyIcon: EmptyIcon = PersonIcon,
  emptyTitle = "No items found",
  emptySubtitle = "Try adjusting your search criteria",
}) => {
  const [query, setQuery] = useState(initialQuery);

  // Width of the horizontal scroll viewport, used to size expanded (collapse)
  // rows so they fill the *visible* area and stay pinned to the left instead of
  // stretching across every column of a very wide table.
  const scrollRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setViewportWidth(el.clientWidth));
    observer.observe(el);
    setViewportWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const actionButtonSx = {
    textTransform: "none",
    height: "40px",
    px: 1.5,
    borderRadius: "calc(-2px + 0.75rem)",
    backgroundColor: "hsl(150 38% 17%)",
    color: "hsl(80 97% 72%)",
    fontSize: 14,
    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
    "& .MuiButton-startIcon": { mr: 0.75 },
    transition: "opacity 0.15s ease",
    "&:hover": {
      backgroundColor: "hsl(150 38% 17%)",
      opacity: 0.9,
    },
    "&:disabled": {
      opacity: 0.4,
      backgroundColor: "hsl(150 38% 17%)",
      color: "hsl(80 97% 72%)",
    },
  };

  const secondaryActionButtonSx = {
    textTransform: "none",
    height: "40px",
    px: 1.5,
    borderRadius: "calc(-2px + 0.75rem)",
    backgroundColor: "#fff",
    color: "hsl(150 30% 12%)",
    border: "1px solid hsl(140 8% 88%)",
    fontSize: 14,
    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
    "& .MuiButton-startIcon": { mr: 0.75 },
    transition: "opacity 0.15s ease",
    "&:hover": {
      backgroundColor: "#fff",
      opacity: 0.8,
    },
    "&:disabled": {
      opacity: 0.4,
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
    },
  };

  const muiPage = (page || 1) - 1;

  const handlePageChange = (_e, newPage) => onPageChange?.(newPage + 1);
  const handleRowsPerPageChange = (e) =>
    onRowsPerPageChange?.(parseInt(e.target.value, 10));

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      onRowSelect(data.map((r) => r.id));
    } else {
      onRowSelect([]);
    }
  };

  const handleCheckboxClick = (event, id) => {
    event.stopPropagation();
    const idx = selectedRows.indexOf(id);
    if (idx === -1) {
      onRowSelect([...selectedRows, id]);
    } else {
      onRowSelect(selectedRows.filter((sid) => sid !== id));
    }
  };

  const isSelected = (id) => selectedRows.includes(id);
  const numSelected = selectedRows.length;
  const rowCount = data.length;

  const totalCols =
    columns.length +
    extraHeadColumns.length +
    tailHeadColumns.length +
    (selectable ? 1 : 0) +
    (expandable ? 1 : 0);

  // Leading expand-arrow cell (leftmost column, before the select checkbox).
  const renderExpandCell = (row) => (
    <TableCell
      key="expand"
      sx={{ width: 36, px: 0.5, py: 1.5 }}
      onClick={(e) => e.stopPropagation()}
    >
      <IconButton
        size="small"
        aria-label={isRowExpanded?.(row) ? "Collapse" : "Expand"}
        onClick={(e) => onToggleExpandRow?.(row, e)}
        sx={{
          color: isRowExpanded?.(row) ? "rgba(42, 51, 78, 0.7)" : "rgba(42, 51, 78, 0.3)",
          "&:hover": { color: "rgba(42, 51, 78, 0.7)" },
        }}
      >
        {isRowExpanded?.(row) ? (
          <KeyboardArrowDownIcon fontSize="small" />
        ) : (
          <KeyboardArrowRightIcon fontSize="small" />
        )}
      </IconButton>
    </TableCell>
  );

  const headCellSx = {
    backgroundColor: "#F9FAFF",
    borderBottom: "2px solid",
    borderColor: "rgba(42, 51, 78, 0.1)",
    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#2A334E",
    py: 1.5,
    whiteSpace: "nowrap",
  };

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minHeight: 0,
        maxHeight: "calc(100vh)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid",
        borderColor: "rgba(42, 51, 78, 0.08)",
        background: "#fff",
        paddingBottom: "45px",
        "&::-webkit-scrollbar": { width: 8, height: 8 },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: 4 },
        "&::-webkit-scrollbar-thumb": {
          background: "#c1c1c1",
          borderRadius: 4,
          "&:hover": { background: "#a1a1a1" },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: "#F2F4F7",
          borderBottom: "1px solid rgba(42, 51, 78, 0.12)",
          px: 3,
          py: 1.25,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{
                color: "black",
                fontSize: 20,
                letterSpacing: "-0.3px",
                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "hsl(140 10% 60%)",
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              }}
            >
              {loading ? (
                <Skeleton width={80} sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />
              ) : (
                totalLabel(totalCount)
              )}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              size="small"
              value={query}
              onChange={(e) => {
                const next = e.target.value;
                setQuery(next);
                onQueryChange?.(next);
              }}
              placeholder={searchPlaceholder}
              aria-label="Search"
              sx={{
                minWidth: 280,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  transition: "all 0.2s ease",
                  height: 40,
                  "&:hover": { backgroundColor: "#fff" },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    boxShadow: "0 0 0 3px rgba(7, 147, 68, 0.2)",
                  },
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "transparent" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#079344",
                    borderWidth: 2,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "#2A334E", opacity: 0.6 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (searchInfo || searchById) ? (
                  <InputAdornment position="end">
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        <Box sx={{ py: 0.25 }}>
                          {searchInfo && (
                            <>
                              <Typography
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  mb: 0.25,
                                }}
                              >
                                Search matches
                              </Typography>
                              <Typography sx={{ fontSize: 12, lineHeight: 1.4 }}>
                                {searchInfo}
                              </Typography>
                            </>
                          )}
                          {searchById && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                lineHeight: 1.4,
                                mt: searchInfo ? 0.75 : 0,
                                opacity: 0.85,
                              }}
                            >
                              Search by ID: type <b>#123</b> or <b>id: 123</b>
                            </Typography>
                          )}
                        </Box>
                      }
                    >
                      <Box
                        component="span"
                        aria-label="Searchable fields"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          cursor: "help",
                          color: "#2A334E",
                          opacity: 0.5,
                          transition: "opacity 0.15s ease",
                          "&:hover": { opacity: 0.9 },
                        }}
                      >
                        <Info size={16} />
                      </Box>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
            {extraActions.map((action) => (
              <Button
                key={action.label}
                variant="contained"
                size="small"
                disableRipple
                disableElevation
                onClick={() => action.onClick?.()}
                disabled={action.disabled}
                startIcon={action.icon}
                sx={secondaryActionButtonSx}
              >
                {action.label}
              </Button>
            ))}
            <Tooltip title={addDisabled && addTooltip ? addTooltip : ""} arrow>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disableRipple
                  disableElevation
                  onClick={() => onAddClick?.()}
                  disabled={!onAddClick || addDisabled}
                  startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  sx={actionButtonSx}
                >
                  {addButtonLabel}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      
      {/* Filters */}
      {filtersComponent}

      {/* Bulk Actions */}
      {selectable && numSelected > 0 && (BulkActionsComponent || onExportCsv) && (
        <Box
          sx={{
            backgroundColor: "hsla(150, 38%, 17%, 0.06)",
            borderBottom: "1px solid hsl(140 8% 88%)",
            px: 3,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: "hsl(150 38% 17%)",
            }}
          >
            {numSelected} selected
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {BulkActionsComponent && (
              <BulkActionsComponent
                selectedRows={selectedRows}
                onClearSelection={() => onRowSelect([])}
                {...bulkActionsProps}
              />
            )}
            {onExportCsv && (
              <Button
                size="small"
                variant="outlined"
                disableRipple
                disableElevation
                onClick={() => onExportCsv(selectedRows)}
                sx={{
                  textTransform: "none",
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  fontSize: 13,
                  borderRadius: "0.6rem",
                  borderColor: "hsl(150 38% 17%)",
                  color: "hsl(150 38% 17%)",
                  "&:hover": {
                    borderColor: "hsl(150 38% 17%)",
                    backgroundColor: "hsla(150, 38%, 17%, 0.08)",
                  },
                }}
              >
                Export CSV
              </Button>
            )}
          </Stack>
        </Box>
      )}

      {/* Table */}
      <TableContainer
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 8, height: 8 },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: 4 },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: 4,
            "&:hover": { background: "#a1a1a1" },
          },
        }}
      >
        <Table stickyHeader size="small" aria-label={`${title} table`}>
          <TableHead>
            <TableRow>
              {expandable && (
                <TableCell sx={{ ...headCellSx, width: 36, px: 0.5 }} />
              )}
              {selectable && (
                <TableCell padding="checkbox" sx={{ ...headCellSx, textAlign: "center" }}>
                  <CircleCheck
                    checked={rowCount > 0 && numSelected === rowCount}
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    onClick={() =>
                      handleSelectAllClick({
                        target: { checked: !(numSelected === rowCount) },
                      })
                    }
                    title="Select all rows"
                  />
                </TableCell>
              )}
              {extraHeadColumns.map((label) => (
                <TableCell key={label} sx={headCellSx}>
                  {label}
                </TableCell>
              ))}
              {columns.map((col) => (
                <TableCell key={col.key} sx={headCellSx}>
                  {col.label}
                </TableCell>
              ))}
              {tailHeadColumns.map((label) => (
                <TableCell key={`tail-${label}`} sx={headCellSx}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(rowsPerPage || 25)].map((_, idx) => (
                <TableRow key={`skeleton-${idx}`}>
                  {expandable && (
                    <TableCell sx={{ width: 36, px: 0.5 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                    </TableCell>
                  )}
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Skeleton variant="circular" width={24} height={24} />
                    </TableCell>
                  )}
                  {extraHeadColumns.map((label) => (
                    <TableCell key={`skeleton-extra-${idx}-${label}`}>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  ))}
                  {columns.map((col) => (
                    <TableCell key={`skeleton-${idx}-${col.key}`}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                  {tailHeadColumns.map((label) => (
                    <TableCell key={`skeleton-tail-${idx}-${label}`}>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalCols} sx={{ textAlign: "center", py: 8 }}>
                  <Box sx={{ color: "text.secondary" }}>
                    <EmptyIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontFamily="Segoe UI Regular, sans-serif, system-ui"
                    >
                      {emptyTitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        opacity: 0.7,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      }}
                    >
                      {emptySubtitle}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => {
                const itemSelected = selectable ? isSelected(row.id) : false;
                const expandedContent = renderExpandedRow?.(row);
                return (
                  <React.Fragment key={row?.id || idx}>
                    <TableRow
                      hover
                      tabIndex={-1}
                      role={onRowClick ? "button" : undefined}
                      selected={itemSelected}
                      onClick={() => onRowClick?.(row)}
                      onKeyDown={(e) => {
                        if (!onRowClick) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(row);
                        }
                      }}
                      sx={{
                        backgroundColor:
                          idx % 2 === 0 ? "#fff" : "rgba(249, 250, 255, 0.5)",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: alpha("#079344", 0.04),
                          transform: "scale(1.0003)",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                        },
                        cursor: onRowClick ? "pointer" : "default",
                        ...(getRowSx ? getRowSx(row) : {}),
                      }}
                    >
                      {expandable && renderExpandCell(row)}
                      {selectable && (
                        <TableCell padding="checkbox" sx={{ textAlign: "center" }}>
                          <CircleCheck
                            checked={itemSelected}
                            onClick={(event) => handleCheckboxClick(event, row.id)}
                            title="Select row"
                          />
                        </TableCell>
                      )}
                      {renderExtraCells?.(row)}
                      {columns.map((col) => (
                        <TableCell
                          key={`${row?.id}-${col.key}`}
                          sx={{
                            whiteSpace: "nowrap",
                            py: 1.5,
                            fontSize: 13,
                            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                            color: "#2A334E",
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {renderCell
                            ? renderCell(row, col.key)
                            : col.render
                              ? col.render(row)
                              : formatValue(row[col.key])}
                        </TableCell>
                      ))}
                      {renderTailCells?.(row)}
                    </TableRow>
                    {expandedContent && (
                      <TableRow>
                        <TableCell colSpan={totalCols} sx={{ p: 0, borderBottom: "1px solid rgba(42, 51, 78, 0.08)" }}>
                          <Box
                            sx={{
                              position: "sticky",
                              left: 0,
                              width: viewportWidth ? `${viewportWidth}px` : "100%",
                            }}
                          >
                            {expandedContent}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "rgba(42, 51, 78, 0.08)",
          backgroundColor: "#F9FAFF",
          position: "fixed",
          bottom: 0,
          right: 0,
          left: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 3,
        }}
      >
        <TablePagination
          component="div"
          count={totalCount || 0}
          page={muiPage}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage || 25}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) =>
            `Showing ${from}–${to} of ${count.toLocaleString()}`
          }
          nextIconButtonProps={{
            "aria-label": "Next page",
            disabled:
              loading ||
              (muiPage + 1) * (rowsPerPage || 25) >= (totalCount || 0),
            sx: { "&:hover": { backgroundColor: alpha("#079344", 0.1) } },
          }}
          backIconButtonProps={{
            "aria-label": "Previous page",
            disabled: loading || muiPage === 0,
            sx: { "&:hover": { backgroundColor: alpha("#079344", 0.1) } },
          }}
          sx={{
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "#2A334E",
              },
            "& .MuiTablePagination-select": { borderRadius: "6px" },
            "& .MuiToolbar-root": { minHeight: "20px !important" },
          }}
        />
      </Box>
    </Paper>
  );
};

export default DataTable;
